import { Account, AccountData, WarehouseSchema } from '..'
import { JsonValue } from '../schema_types/common'
import {
    EntityData,
    ConversionAttributesData,
    EntityType,
    SharedBudgetsData,
    SubEntityData,
    SubEntityType,
    SharedBiddingStrategiesData,
} from '../account-data'
import { AdGroupAttributes, CampaignAttributes } from '../schema_types/warehouse'

export namespace UpdateCampaignStatus {
    export type Request = {
        accountId: Account.ID
        campaigns: {
            campaignId: string
            status: CampaignAttributes['status']
        }[]
        userId: number
    }
}

export namespace UpdateAdGroupStatus {
    export type Request = {
        accountId: Account.ID
        adGroups: {
            adGroupId: string
            status: AdGroupAttributes['status']
        }[]
        userId: number
    }
}

export namespace UpdateCampaignBudget {
    export type UpdateBudgetBody =
        | {
              budgetAmount: number
              isSharedBudget: true
              sharedBudgetId: number
              name: string
              createNew: false
              budgetType?: 'DAILY' | 'LIFETIME'
          }
        | {
              budgetAmount: number
              isSharedBudget: true
              name: string
              createNew: true
              budgetType?: 'DAILY' | 'LIFETIME'
          }
        | {
              isSharedBudget: false
              budgetAmount: number
              budgetType?: 'DAILY' | 'LIFETIME'
          }

    export type Request = {
        accountId: Account.ID
        userId: number
        campaignIds: string[]
        budget: UpdateBudgetBody
    }

    export type Response = Omit<SharedBudgetsData, 'referenceCount'> & {
        budgetType?: 'DAILY' | 'LIFETIME'
    }
    export type ErrorResponse = { success: false; error: string }
}

export namespace UpdateAdGroupBudget {
    export type UpdateBudgetBody = {
        budgetAmount: number
        budgetType?: 'DAILY' | 'LIFETIME'
    }

    export type Request = {
        accountId: Account.ID
        userId: number
        adGroupIds: string[]
        budget: UpdateBudgetBody
    }

    export type Response = UpdateBudgetBody
}

export namespace UpdateCampaignBiddingStrategy {
    export type Scheme = Record<string, JsonValue>
    export type UpdateBiddingStrategyBody =
        | {
              scheme: Scheme
              isPortfolioBiddingStrategy: true
              sharedBiddingStrategyId: number
              name: string
              type: WarehouseSchema.CampaignSharedBiddingStrategies['type']
              status: WarehouseSchema.CampaignSharedBiddingStrategies['status']
              createNew: false
          }
        | {
              scheme: Scheme
              isPortfolioBiddingStrategy: true
              name: string
              type: WarehouseSchema.CampaignSharedBiddingStrategies['type']
              status: WarehouseSchema.CampaignSharedBiddingStrategies['status']
              createNew: true
          }
        | {
              type: WarehouseSchema.CampaignSharedBiddingStrategies['type']
              isPortfolioBiddingStrategy: false
              scheme: Scheme
          }

    export type Request = {
        accountId: Account.ID
        userId: number
        campaignIds: string[]
        biddingStrategy: UpdateBiddingStrategyBody
    }

    export type Response = Omit<SharedBiddingStrategiesData, 'referenceCount'> | undefined
    export type ErrorResponse = { success: false; error: string }
}

// Ad-group-scoped bidding strategy updates. Unlike UpdateCampaignBiddingStrategy, there's no
// portfolio/shared strategy concept here - see updatePlatformAdGroupBiddingStrategyChatGpt in
// adgroups.put.ts, the only current implementer.
export namespace UpdateAdGroupBiddingStrategy {
    export type Scheme = Record<string, JsonValue>
    export type Request = {
        accountId: Account.ID
        userId: number
        adGroupIds: string[]
        biddingStrategy: {
            type: WarehouseSchema.CampaignSharedBiddingStrategies['type']
            scheme: Scheme
        }
    }

    export type Response = {
        type: WarehouseSchema.CampaignSharedBiddingStrategies['type']
        scheme: Scheme
    }
    export type ErrorResponse = { success: false; error: string }
}

export namespace UpdateEntitySetting {
    export type UpdateSettingBody = {
        campaignIds: string[]
        adGroupIds?: string[]
        settings: Partial<AccountData.EntityData['settings']>
    }

    export type Request = {
        accountId: Account.ID
        settingBody: UpdateSettingBody
        userId: number
    }

    export type Response = Request
}

export namespace GetAccountData {
    export enum LookbackWindow {
        LAST_30_DAYS = 'LAST_30_DAYS',
        LAST_90_DAYS = 'LAST_90_DAYS',
        LAST_180_DAYS = 'LAST_180_DAYS',
        LAST_YEAR = 'LAST_YEAR',
        MONTH_TO_DATE = 'MONTH_TO_DATE',
    }

    export type GetAccountDataFilters = {
        campaignIds: string[]
        conversionActionIds: string[]
        entity: EntityType
        dateRange?: {
            fromDate: string // YYYY-MM-DD
            toDate: string // YYYY-MM-DD
        }
        lookbackWindow?: LookbackWindow
    }

    export type Request = { accountId: Account.ID; filters: GetAccountDataFilters }

    export type Response = {
        rows: EntityData[]
        conversionActions: ConversionAttributesData[]
        sharedBudgets: SharedBudgetsData[]
        sharedBiddingStrategies: SharedBiddingStrategiesData[]
    }
}

export namespace GetAccountSubEntityData {
    export enum LookbackWindow {
        LAST_30_DAYS = 'LAST_30_DAYS',
        LAST_90_DAYS = 'LAST_90_DAYS',
        LAST_180_DAYS = 'LAST_180_DAYS',
        LAST_YEAR = 'LAST_YEAR',
    }

    export type GetAccountSubEntityDataBaseFilters = {
        entity: SubEntityType
        lookbackWindow: LookbackWindow
    }

    export type GetAccountKeywordsDataFiltersForCampaigns = GetAccountSubEntityDataBaseFilters & {
        source: EntityType.CAMPAIGN
        campaignId: string
        conversionActionIds: string[]
    }

    export type GetAccountKeywordsDataFiltersForAdGroups = GetAccountSubEntityDataBaseFilters & {
        source: EntityType.AD_GROUP
        adGroupId: string
        conversionActionIds: string[]
    }

    export type GetAccountSubEntityDataFilters =
        | GetAccountKeywordsDataFiltersForCampaigns
        | GetAccountKeywordsDataFiltersForAdGroups

    export type Request = { accountId: Account.ID; filters: GetAccountSubEntityDataFilters }

    export type Response = {
        rows: SubEntityData[]
    }
}

export namespace GetHighCpcSearchTerms {
    export type Request = {
        accountId: Account.ID
        campaignId: string
        conversionActionIds: string[]
        lookbackWindow: GetAccountSubEntityData.LookbackWindow
    }

    export type Response = {
        rows: AccountData.highCpcSearchTerms[]
    }
}

export namespace GetAccountSubEntityCount {
    export type GetAccountSubEntityCountFiltersForCampaigns = {
        source: EntityType.CAMPAIGN
        campaignIds: string[]
    }

    export type GetAccountSubEntityCountFiltersForAdGroups = {
        source: EntityType.AD_GROUP
        adGroupIds: string[]
    }

    export type GetAccountSubEntityCountFilters =
        | GetAccountSubEntityCountFiltersForCampaigns
        | GetAccountSubEntityCountFiltersForAdGroups

    export type Request = { accountId: Account.ID; filters: GetAccountSubEntityCountFilters }

    export type Response = Record<
        string,
        {
            adGroupCount: number
            keywordCount: number
            searchTermCount: number
            negatives: SubEntityData[]
            exclusions: SubEntityData[]
        }
    >
}
